import React from 'react';

import { COMPARE_LABEL, type Shown, delta, formatValue } from '../format';
import type { Envelope, WindowOut } from '../types';

type ProseProps = { text: string; envelopes: Array<Envelope>; period: { label: string; current: WindowOut; comparisons: Array<WindowOut> }; shown: Shown };

const TOKEN = /^[ \t]*#{1,6}[ \t]+([^\n]+)$|\{([^}]+)\}|\*\*([^*]+)\*\*/gm;
const AUTHORED_COMPARISON = /^\s*(vs\b|versus\b|compared\b|against\b)/i;

export default function Prose({ text, envelopes, period, shown }: ProseProps) {
  const plain = (source: string): Array<React.ReactNode> => source.split('\n').flatMap((line, index) => index ? [<br />, line] : [line]);
  const render = (source: string): Array<React.ReactNode> => {
    const parts: Array<React.ReactNode> = [];
    let last = 0;
    for (const match of source.matchAll(TOKEN)) {
      parts.push(...plain(source.slice(last, match.index)));
      last = (match.index ?? 0) + match[0].length;
      if (match[1] || match[3]) {
        parts.push(<b key={last}>{render(match[1] ?? match[3])}</b>);
        continue;
      }
      const [name, verb, against] = match[2].trim().split(/\s+/);
      if (name === 'period') {
        parts.push(verb === 'compare' ? period.comparisons.map((window) => window.label).join(' and ') : period.current.label);
        continue;
      }
      if (name === 'weeks') {
        parts.push(period.current.weeks ?? period.current.label);
        continue;
      }
      const envelope = envelopes.find((candidate) => candidate.columns.some((entry) => entry.name === name || entry.name.endsWith(`.${name}`) || name.endsWith(`.${entry.name}`)));
      const column = envelope?.columns.find((entry) => entry.name === name || entry.name.endsWith(`.${name}`) || name.endsWith(`.${entry.name}`));
      const value = column ? (envelope?.totals?.current?.[column.name] as number | null) : undefined;
      if (!column || !envelope) {
        parts.push('–');
        continue;
      }
      if (verb === 'vs' || verb === 'delta') {
        const id = against ?? 'prior';
        const change = delta(value, envelope.totals?.[id]?.[column.name] as number | null, column, shown.delta === 'none' ? 'pct' : shown.delta, shown.numbers);
        parts.push(
          <b className={change?.tone} data-lens-ref={`${envelope.query}.${column.name}@${id}`} key={last}>
            {change ? `${change.arrow} ${change.text}` : '–'}
          </b>,
          AUTHORED_COMPARISON.test(source.slice(last)) ? '' : ` ${COMPARE_LABEL[id] ?? ''}`,
        );
        continue;
      }
      parts.push(
        <b data-lens-ref={`${envelope.query}.${column.name}`} key={last}>
          {formatValue(value, column.format, { numbers: shown.numbers, decimals: column.decimals })}
        </b>,
      );
    }
    parts.push(...plain(source.slice(last)));
    return parts.map((part, index) => <React.Fragment key={index}>{part}</React.Fragment>);
  };
  return (
    <div className="prose">{render(text)}</div>
  );
}
